/**
 * Die Zughistorie auf dem Bildschirm — §56, §67 Schritt 47.
 *
 * **Interne Kennungen haben hier nichts zu suchen.** Im Ereignis stehen `p1-guard-1` und
 * `{q: 2, r: 0}`; auf dem Bildschirm stehen „Wächter" und `green-C3`. Die Klasse kommt aus der
 * Zuordnung der Partie, das Feld über das Layout der Engine — dieselbe kanonische Benennung wie
 * in der Referenzgrafik, in der Zugliste und ab V2 im KI-Kontext.
 *
 * **Was sich nicht auflösen lässt, bleibt stehen, wie es ist.** Eine Kennung ist verständlicher
 * als eine Lücke — dieselbe Entscheidung wie bei den Anzeigenamen. Vorkommen kann das bei einer
 * Figur, die der Zustand nicht mehr führt.
 *
 * **Das Älteste steht oben.** Eine Historie liest sich vorwärts; die Reihenfolge ist die
 * Ereignisnummer und nicht die Zugnummer, weil ein Zug mehrere Ereignisse haben kann
 * (`game/history.ts`).
 *
 * **Previews stehen nicht darin** (§41, §57). Das setzt schon die Datenbank durch — hier ist
 * nichts zu filtern.
 */

import { useTranslation } from 'react-i18next';

import { cellId, type PieceType, type SectorLayout } from '@flaghex/engine';

import { ownedPieceName } from './ownedPiece.js';
import { formatDuration } from './status.js';
import type { HistoryEntry } from './history.js';

export interface HistoryPanelProps {
  readonly entries: readonly HistoryEntry[];
  readonly names: ReadonlyMap<string, string>;
  readonly pieceTypes: ReadonlyMap<string, PieceType>;
  readonly layout: SectorLayout;
  /**
   * Wessen Züge ein Bot gemacht hat (**E64**, Antwort 4).
   *
   * **Kommt von außen wie die Namen**, und aus demselben Grund: das Ereignis trägt nur eine
   * Spieler-ID. Die Steuerung steht am Spieler im Zustand — eine Historie, die sie selbst
   * herleiten müsste, bräuchte das Brett dazu.
   */
  readonly bots: ReadonlySet<string>;
}

export function HistoryPanel({ entries, names, pieceTypes, layout, bots }: HistoryPanelProps) {
  const { t } = useTranslation(['play', 'pieces']);

  /**
   * Der Anzeigename einer Figur.
   *
   * **Das Ereignis geht vor der Zuordnung**, wenn es die Klasse mitführt (seit 11d, siehe
   * `history.ts`): die Zuordnung kommt aus dem **aktuellen** Zustand, und der kennt eine Figur
   * nicht mehr, die vom Brett verschwunden ist oder die eine Pause verdeckt. Das Ereignis
   * dagegen ist von sich aus lesbar — und eine Historie, die man nur mit dem passenden Brett
   * lesen kann, ist keine.
   */
  const pieceName = (id: string | undefined, fromEvent?: string | undefined): string => {
    if (id === undefined) return '';
    const type = fromEvent ?? pieceTypes.get(id);
    return type === undefined ? id : t(`pieces:${type}`);
  };

  /**
   * Welche Textschlüssel für dieses Ereignis in Frage kommen — in dieser Reihenfolge.
   *
   * Zwei Ereignisse haben mehr als einen: ein **Randwurf** ist etwas anderes als eine
   * Verschiebung (in der Sichtprüfung zu 10h fiel auf, dass die Historie beides gleich nannte),
   * und ein **Debug-Eingriff** hat einen Text je Art mit einem allgemeinen als Rückfall
   * (**E42**). Mehrere Schlüssel an `t` zu übergeben ist genau dafür gemacht: der erste
   * vorhandene gewinnt — eine Art, die noch keinen eigenen Text hat, bekommt den allgemeinen
   * statt einer leeren Zeile.
   */
  // `string[]` und nicht `readonly string[]`: `t` nimmt genau diesen Typ (i18next).
  const keysOf = (entry: HistoryEntry): string[] => {
    if (entry.type === 'PUSH' && entry.offBoard) return ['play:history.PUSH_OFF'];
    if (entry.type === 'GAME_FINISHED') {
      // **Drei Fälle, drei Sätze** (Befund B2, R5): ein Sieg, ein Unentschieden, und ein
      // Ereignis, das den Ausgang nicht mitführt. Der letzte ist kein Sonderfall, sondern eine
      // alte Partie — und er bekommt den allgemeinen Satz statt „undefined gewinnt".
      if (entry.winner !== undefined) return ['play:history.GAME_FINISHED_WON'];
      if (entry.drawn) return ['play:history.GAME_FINISHED_DRAW'];
      return ['play:history.GAME_FINISHED'];
    }
    if (entry.type === 'DEBUG_INTERVENTION' && entry.op !== undefined) {
      // Ein **erzwungener** Eingriff ist etwas anderes als ein gewöhnlicher (Nr. 38: die
      // Markierung „regelwidrig erzeugt" ist ausdrücklich sichtbar und nicht nur im Log). Eine
      // Eingriffsart ohne eigenen erzwungenen Text fällt auf den gewöhnlichen zurück.
      const forced = entry.overridden.length > 0 ? [`play:history.DEBUG_${entry.op}_FORCED`] : [];
      return [...forced, `play:history.DEBUG_${entry.op}`, 'play:history.DEBUG_INTERVENTION'];
    }
    return [`play:history.${entry.type}`];
  };

  /**
   * Der Anzeigename des **betroffenen** Spielers (11f).
   *
   * Dieselbe Zuordnung wie bei einem Zug, und dieselbe Rückfallregel wie bei den Figuren: wen
   * sie nicht kennt, steht mit seiner Kennung da — eine Kennung ist verständlicher als eine
   * Lücke. In der Zeile selbst steht trotzdem kein Handelnder: eingegriffen hat niemand
   * (Nr. 37), und `lineGame` lässt den Spieler deshalb ganz weg.
   */
  const subjectName = (id: string | undefined): string =>
    id === undefined ? '' : (names.get(id) ?? id);

  /**
   * Der Handelnde einer Zeile — mit dem Abzeichen, wenn es ein Bot ist (**E64**, Antwort 4).
   *
   * **In jeder Zeile, nicht nur in der ersten.** Die Historie ist ab V3 Balancing-Material, das
   * **allein** gelesen wird; eine Kennzeichnung nur am Anfang macht die Mitte einer Aufzeichnung
   * mehrdeutig. Und weil der Host den Namen frei tippt (Antwort 3), ist eine Bot-Zeile sonst von
   * der eines Menschen nicht zu unterscheiden.
   *
   * **Eine Zeile ohne Handelnden bekommt nichts** — `lineGame` nennt keinen Spieler
   * (Partieende, Zeitablauf, Eingriff), und ein Abzeichen ohne Namen wäre eine Behauptung über
   * niemanden. Das entscheidet der Aufrufer, indem er `who` dort nicht fragt.
   */
  const who = (id: string): string => {
    const name = names.get(id) ?? id;
    return bots.has(id) ? t('play:history.botWho', { name }) : name;
  };

  const what = (entry: HistoryEntry): string =>
    t(keysOf(entry), {
      piece: pieceName(entry.piece, entry.pieceType),
      // **Das Opfer mit seinem Besitzer** (B6): seit R2 kann es die eigene Figur sein, und
      // „Anna: Stoßer stößt Stoßer" ließ offen, welche. Ohne Angabe steht die Figur allein da.
      victim: ownedPieceName(
        t,
        entry.victimOwner === undefined ? undefined : subjectName(entry.victimOwner),
        pieceName(entry.victim, entry.victimType),
      ),
      cell: entry.to === undefined ? '' : cellId(layout, entry.to),
      // Wo die gestoßene Figur **landet** (B1). Bei einem Stoß über den Rand gibt es kein
      // Landefeld — dort nennt `PUSH_OFF` den Rand und fragt `{{target}}` gar nicht ab.
      target: entry.victimTo === undefined ? '' : cellId(layout, entry.victimTo),
      // **Dieselbe Auflösung wie im Statusbereich** (`winnerLabel`): wen die Namensliste kennt,
      // steht mit seinem Namen da, alles andere ist eine Mannschaft. Ein Team gewinnt als Team
      // (§23), und eine Kennung wäre hier so unlesbar wie `p1-guard-1` in einer Zugzeile.
      winner:
        entry.winner === undefined
          ? ''
          : (names.get(entry.winner) ?? t('play:teamName', { team: entry.winner })),
      reason: entry.reason === undefined ? '' : t(`play:reason.${entry.reason}`),
      score: entry.score?.to ?? '',
      subject: subjectName(entry.subject),
      // **Eine Zeit ist `m:ss`, nicht eine Zahl** — und zwar mit derselben Formatierung wie
      // die Schachuhr daneben (`status.ts`: „zwei Formatierungen derselben Größe wären zwei
      // Schreibweisen für dasselbe").
      time: entry.remainingMs === undefined ? '' : formatDuration(entry.remainingMs),
    });

  return (
    <section data-history="" data-panel="history">
      <h3>{t('play:history.heading')}</h3>
      {entries.length === 0 ? (
        <p>{t('play:history.empty')}</p>
      ) : (
        <ul>
          {entries.map((entry) => (
            <li key={entry.seq}>
              {entry.player === null
                ? t('play:history.lineGame', { turn: entry.turnNumber, what: what(entry) })
                : t('play:history.line', {
                    turn: entry.turnNumber,
                    who: who(entry.player),
                    what: what(entry),
                  })}
              {/* Der Rückfall eines LLM-Bots (`L3b`, **E112** Antwort 2) — hinter der Zeile. */}
              {entry.fallback === undefined ? null : (
                <span data-field="fallback">
                  {' '}
                  {entry.fallback.reason === undefined
                    ? t('play:history.fallback')
                    : t('play:history.fallbackWhy', {
                        reason: t(`lobby:bot.badge.reasons.${entry.fallback.reason}`),
                      })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
